import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import {
  MOBILE_SCREEN,
  COLOR_OVERLAY_BACKGROUND,
  INFORMATIONAL_MESSAGE_POPUP_BACKGROUND_COLOR_GRADIENT,
  COLOR_TERTIARY,
  FONT_SIZE_MEDIUM,
  FONT_SIZE_MEDIUM_MOBILE,
  COLOR_APP_TEXT,
} from "../theme";
import { SecondaryButton } from "../buttons/SecondaryButton";
import { PrimaryButton } from "../buttons/PrimaryButton";
import { ParagraphSmall } from "../typography/Paragraph";
import { PopupHeader } from "../typography/Header";
import Loader from "../icons/Loader";

export const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  overflow-y: auto;
  background: ${COLOR_OVERLAY_BACKGROUND};
  z-index: 1000;
`;

export const PopupContainer = styled.div<{ backgroundColor?: string }>`
  position: relative;
  top: 10%;
  left: 50%;
  transform: translate(-50%);
  max-width: 944px;
  width: 90%;
  background: ${(props) => props.backgroundColor};
  display: flex;
  box-sizing: border-box;
  flex-direction: column;
  align-items: center;
  z-index: 1;
  border-radius: 16px;
  text-align: center;
  gap: 16px;
  padding: 32px 64px 32px 64px;
  margin-bottom: 192px;
  ${MOBILE_SCREEN} {
    padding: 32px;
  }
`;

const IconContainer = styled.div`
  display: flex;
  border-radius: 100px;
  background: ${COLOR_TERTIARY};
  align-items: center;
  width: 48px;
  height: 48px;
  min-height: 48px;
  justify-content: center;
`;

export const ButtonContainer = styled.div<{ singleButton?: boolean }>`
  margin-top: 32px;
  display: flex;
  gap: 24px;
  width: 90%;
  ${(props) => props.singleButton && `justify-content: center;`}
  ${MOBILE_SCREEN} {
    margin-top: 48px;
    flex-direction: column-reverse;
    gap: 16px;
  }
`;

export const PopupPrimaryButton = styled(PrimaryButton)`
  flex: 1;
  width: auto;
  height: auto;
  box-sizing: border-box;
  padding: 16px 24px 16px 24px;
  font-size: ${FONT_SIZE_MEDIUM};
  ${MOBILE_SCREEN} {
    font-size: ${FONT_SIZE_MEDIUM_MOBILE};
  }
`;

export const PopupSecondaryButton = styled(SecondaryButton)`
  flex: 1;
  width: auto;
  height: auto;
  box-sizing: border-box;
  padding: 16px 24px 16px 24px;
  font-size: ${FONT_SIZE_MEDIUM};
  ${MOBILE_SCREEN} {
    font-size: ${FONT_SIZE_MEDIUM_MOBILE};
  }
`;

export interface PopupProps {
  backgroundColor?: string;
  icon?: React.ReactElement;
  footer?: React.ReactElement;
  title?: string | React.ReactElement;
  description?: string | React.ReactElement;
  buttons?:
    | React.ReactElement
    | {
        confirmText: string;
        cancelText?: string;
        onConfirm: () => Promise<void> | void;
        onCancel?: () => void;
      };
}

const Popup: React.FC<PopupProps> = ({
  backgroundColor = INFORMATIONAL_MESSAGE_POPUP_BACKGROUND_COLOR_GRADIENT,
  icon,
  title,
  description,
  buttons,
  footer,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const hasDefaultButtons =
    buttons && "confirmText" in buttons && "onConfirm" in buttons;

  const handleConfirm = async () => {
    setIsLoading(true);
    if (buttons && "onConfirm" in buttons) {
      await buttons.onConfirm();
    }
    setIsLoading(false);
  };

  return (
    <Overlay>
      <PopupContainer
        backgroundColor={backgroundColor}
        data-testid="popup-container"
      >
        {icon && <IconContainer>{icon}</IconContainer>}
        {typeof title === "string" ? <PopupHeader>{title}</PopupHeader> : title}
        {typeof description === "string" ? (
          <ParagraphSmall color={COLOR_APP_TEXT}>{description}</ParagraphSmall>
        ) : (
          description
        )}
        {hasDefaultButtons ? (
          <ButtonContainer singleButton={!buttons.cancelText}>
            {buttons.cancelText && buttons.onCancel && (
              <PopupSecondaryButton
                color={COLOR_APP_TEXT}
                disabled={isLoading}
                onClick={buttons.onCancel}
                data-testid="popup-cancel"
              >
                {buttons.cancelText}
              </PopupSecondaryButton>
            )}
            <PopupPrimaryButton
              disabled={isLoading}
              onClick={handleConfirm}
              data-testid="popup-confirm"
            >
              {isLoading ? (
                <Loader size={12} spinnerOnly />
              ) : (
                buttons.confirmText
              )}
            </PopupPrimaryButton>
          </ButtonContainer>
        ) : (
          buttons
        )}
        {footer && footer}
      </PopupContainer>
    </Overlay>
  );
};

export default Popup;
